import React from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { onboardingApi } from '../api/onboarding';
import { useUser } from '../contexts/UserContext';

export const SubscriptionSuccess: React.FC = () => {
    const navigate = useNavigate();
    const { refreshStatus } = useUser();
    const [activating, setActivating] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [retryCount, setRetryCount] = React.useState(0);

    const activate = React.useCallback(async () => {
        setActivating(true);
        setError(null);
        try {
            // Brief pause to allow Stripe webhook to arrive first
            await new Promise(resolve => setTimeout(resolve, 2000));
            await onboardingApi.activate();
            await refreshStatus();
            // Navigate straight to dashboard — OnboardingGuard will now pass
            navigate('/');
        } catch (err: any) {
            const detail = err?.response?.data?.detail || err?.message || 'Activation failed';
            setError(detail);
            setActivating(false);
        }
    }, [navigate, refreshStatus]);

    React.useEffect(() => {
        activate();
    }, [activate]);

    return (
        <Container maxWidth="sm" sx={{ mt: 10 }}>
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                {activating ? (
                    <>
                        <CircularProgress size={60} sx={{ mb: 4 }} />
                        <Typography variant="h5" gutterBottom>
                            Activating Your Account...
                        </Typography>
                        <Typography color="textSecondary">
                            We're confirming your Stripe payment and activating your account. This will only take a moment.
                        </Typography>
                    </>
                ) : error ? (
                    <>
                        <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                            <Typography variant="subtitle2" gutterBottom>Payment received, but activation needs a moment</Typography>
                            <Typography variant="body2">{error}</Typography>
                        </Alert>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Your payment was processed by Stripe. If activation fails, your subscription is still active — click Retry in a few seconds.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => { setRetryCount(c => c + 1); activate(); }}
                            sx={{ mr: 2 }}
                        >
                            {retryCount > 0 ? `Retry (${retryCount})` : 'Retry Activation'}
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/onboarding')}>
                            Back to Onboarding
                        </Button>
                    </>
                ) : (
                    <>
                        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
                        <Typography variant="h4" gutterBottom>
                            Payment Successful!
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4 }} color="textSecondary">
                            Your subscription is now active. Redirecting to your dashboard...
                        </Typography>
                    </>
                )}
            </Paper>
        </Container>
    );
};
